import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { LayoutDashboard, FolderOpen, Users, MessageSquare, ArrowLeft, LogOut } from "lucide-react";

const LOGO_URL = "https://custom-images.strikinglycdn.com/res/hrscywv4p/image/upload/c_limit,fl_lossy,h_300,w_300,f_auto,q_auto/17784585/525540_804907.png";

const navItems = [
  { label: "Overview", to: "/admin", icon: LayoutDashboard },
  { label: "Projects", to: "/admin/projects", icon: FolderOpen },
  { label: "Users", to: "/admin/users", icon: Users },
  { label: "Requests", to: "/admin/requests", icon: MessageSquare },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-slate-50 flex" data-testid="admin-layout">
      {/* Sidebar */}
      <aside className="w-60 bg-white border-r border-slate-200 fixed inset-y-0 left-0 z-40 flex flex-col">
        {/* Logo */}
        <div className="p-5 border-b border-slate-100">
          <Link to="/" className="flex items-center gap-2">
            <img src={LOGO_URL} alt="SupraLife" className="h-7 w-7" />
            <span className="font-serif text-sm font-bold text-slate-900 tracking-tight">
              SupraLife<span className="text-slate-400 font-normal">.Admin</span>
            </span>
          </Link>
        </div>

        {/* Nav */}
        <nav className="flex-1 p-3 space-y-1">
          {navItems.map((item) => {
            const isActive = item.to === "/admin"
              ? location.pathname === "/admin"
              : location.pathname.startsWith(item.to);
            return (
              <Link
                key={item.to}
                to={item.to}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-sans transition-colors ${
                  isActive
                    ? "bg-slate-900 text-white"
                    : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                }`}
                data-testid={`admin-nav-${item.label.toLowerCase()}`}
              >
                <item.icon className="w-4 h-4" />
                {item.label}
              </Link>
            );
          })}
        </nav>

        {/* Bottom */}
        <div className="p-3 border-t border-slate-100 space-y-1">
          <Link
            to="/dashboard"
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-sans text-slate-500 hover:bg-slate-50 hover:text-slate-900 transition-colors"
            data-testid="admin-back-to-app"
          >
            <ArrowLeft className="w-4 h-4" /> Back to App
          </Link>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-sans text-slate-400 hover:bg-red-50 hover:text-red-500 transition-colors"
            data-testid="admin-logout-btn"
          >
            <LogOut className="w-4 h-4" /> Log Out
          </button>
        </div>
      </aside>

      {/* Content */}
      <main className="flex-1 ml-60">
        <div className="p-8 md:p-12">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
